import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Users,
    GraduationCap,
    BookOpenText,
    CreditCard,
    BookOpen,
    UserPlus,
    HelpCircle,
    ArrowUpRight,
    Calendar,
    Clock,
    TrendingUp,
} from "lucide-react";
import { FaGraduationCap } from "react-icons/fa";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";
import moment from "moment";
import { getReportsSummary } from "../../utils/admin";
import { getUsers } from "../../utils/user";
import { getSubs } from "../../utils/sub";

const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
};
const getGreetingEmoji = () => {
    const h = new Date().getHours();
    if (h < 12) return "🌅";
    if (h < 17) return "☀️";
    return "🌙";
};

const SectionHeader = ({ children, accent = "from-[#2148C0] to-blue-400" }) => (
    <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-5 flex items-center">
        <div className={`w-2 h-7 md:h-8 bg-gradient-to-b ${accent} rounded-full mr-3`} />
        {children}
    </h3>
);

const StatTile = ({ icon: Icon, label, value, sub, color, to }) => {
    const inner = (
        <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 h-full">
            <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl ${color}`}>
                    <Icon className="w-6 h-6" />
                </div>
                {to && (
                    <ArrowUpRight
                        size={18}
                        className="text-gray-300 group-hover:text-[#2148C0] transition-colors"
                    />
                )}
            </div>
            <p className="text-gray-500 text-sm mb-1">{label}</p>
            <h3 className="text-3xl font-bold text-gray-900 tracking-tight">
                {Number(value || 0).toLocaleString()}
            </h3>
            {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
    );
    return to ? (
        <Link to={to} className="group block">
            {inner}
        </Link>
    ) : (
        inner
    );
};

const AdminDashboard = () => {
    const [summary, setSummary] = useState(null);
    const [users, setUsers] = useState([]);
    const [subs, setSubs] = useState([]);
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        try {
            const a = JSON.parse(localStorage.getItem("admin") || "null");
            if (a) setAdmin(a);
        } catch (_) { /* ignore */ }

        const load = async () => {
            const token = localStorage.getItem("token");
            try {
                const [s, u, sb] = await Promise.all([
                    getReportsSummary(token),
                    getUsers(token).catch(() => ({ users: [] })),
                    getSubs(token).catch(() => ({ subscriptions: [] })),
                ]);
                setSummary(s?.data || null);
                setUsers(u?.users || []);
                setSubs(sb?.subscriptions || []);
            } catch (err) {
                console.error(err);
                setError("Failed to load dashboard data.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const signupChart = useMemo(() => {
        if (!users?.length) return [];
        const buckets = {};
        users.forEach((u) => {
            const d = moment(u.createdAt || u.created_at);
            if (!d.isValid()) return;
            const key = d.format("YYYY-MM-DD");
            buckets[key] = (buckets[key] || 0) + 1;
        });
        return Object.entries(buckets)
            .map(([date, count]) => ({ date, count }))
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .slice(-30);
    }, [users]);

    const activeSubs = subs.filter((s) => s.status === "Active").length;
    const pendingSubs = subs.filter((s) => s.status === "Pending").length;
    const totalUsers = summary?.users?.total ?? users.length;

    return (
        <div>
            {/* Welcome / Hero — matches teacher dashboard style */}
            <div className="mb-8 relative">
                <div className="bg-gradient-to-r from-[#2148C0] to-blue-600 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden shadow-lg shadow-[#2148C0]/20">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20" />
                    <div className="absolute bottom-0 left-0 w-28 h-28 bg-white/5 rounded-full -ml-14 -mb-14" />
                    <div className="absolute top-4 right-4 w-32 h-32 bg-white/5 rounded-full blur-xl" />
                    <div className="absolute bottom-4 left-4 w-24 h-24 bg-purple-400/30 rounded-full blur-lg" />

                    <div className="relative z-10">
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <h2 className="text-3xl md:text-5xl font-bold mb-3 flex items-center">
                                    {getGreeting()},{" "}
                                    {admin?.firstName || "Admin"} {getGreetingEmoji()}
                                </h2>
                                <p className="text-blue-100 text-base md:text-lg opacity-90">
                                    Here's how Teechaa is doing today.
                                </p>
                            </div>
                            <div className="hidden md:block p-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                                <TrendingUp className="w-12 h-12 text-white" />
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 text-blue-100">
                            <span className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 backdrop-blur-sm">
                                <Clock className="w-4 h-4" />
                                <span className="font-medium">
                                    {new Date().toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })}
                                </span>
                            </span>
                            <span className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 backdrop-blur-sm">
                                <Calendar className="w-4 h-4" />
                                <span className="font-medium">
                                    {new Date().toLocaleDateString("en-US", {
                                        weekday: "long",
                                        month: "long",
                                        day: "numeric",
                                    })}
                                </span>
                            </span>
                            <span className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 backdrop-blur-sm">
                                <FaGraduationCap className="w-4 h-4" />
                                <span className="font-medium">
                                    {Number(totalUsers).toLocaleString()} Users
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div
                            key={i}
                            className="h-36 rounded-2xl bg-white border border-gray-100 animate-pulse"
                        />
                    ))}
                </div>
            ) : (
                <>
                    <SectionHeader>Platform overview</SectionHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                        <StatTile
                            icon={Users}
                            label="Total Users"
                            value={totalUsers}
                            sub={`${summary?.users?.students || 0} students · ${summary?.users?.parents || 0} parents`}
                            color="bg-blue-50 text-[#2148C0]"
                            to="/users"
                        />
                        <StatTile
                            icon={GraduationCap}
                            label="Teachers"
                            value={summary?.users?.teachers || 0}
                            sub="View classes & students"
                            color="bg-purple-50 text-purple-600"
                            to="/teachers"
                        />
                        <StatTile
                            icon={BookOpenText}
                            label="Questions"
                            value={summary?.questions?.total || 0}
                            sub={`${summary?.questions?.quizbank || 0} quiz · ${summary?.questions?.exams || 0} exams`}
                            color="bg-green-50 text-green-600"
                            to="/topics"
                        />
                        <StatTile
                            icon={CreditCard}
                            label="Active Subscriptions"
                            value={activeSubs}
                            sub={`${pendingSubs} pending`}
                            color="bg-yellow-50 text-yellow-600"
                            to="/subscription"
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Signup chart */}
                        <div className="lg:col-span-2 bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
                            <div className="flex items-center justify-between mb-5">
                                <SectionHeader accent="from-blue-500 to-indigo-500">
                                    New signups (30 days)
                                </SectionHeader>
                            </div>
                            {signupChart.length === 0 ? (
                                <div className="h-64 grid place-items-center text-gray-400 text-sm">
                                    No signups yet.
                                </div>
                            ) : (
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={signupChart}>
                                            <defs>
                                                <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#2148C0" stopOpacity={0.4} />
                                                    <stop offset="100%" stopColor="#2148C0" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                                            <XAxis
                                                dataKey="date"
                                                stroke="#94a3b8"
                                                tickFormatter={(d) => moment(d).format("MMM D")}
                                                fontSize={12}
                                            />
                                            <YAxis stroke="#94a3b8" fontSize={12} />
                                            <Tooltip
                                                contentStyle={{
                                                    borderRadius: 12,
                                                    border: "1px solid #e2e8f0",
                                                    boxShadow: "0 10px 30px rgba(33,72,192,0.12)",
                                                }}
                                                labelFormatter={(d) => moment(d).format("MMM D, YYYY")}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="count"
                                                stroke="#2148C0"
                                                strokeWidth={2.5}
                                                fill="url(#signupGrad)"
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>

                        {/* Quick links */}
                        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
                            <SectionHeader accent="from-green-500 to-blue-500">
                                Quick reports
                            </SectionHeader>
                            <div className="space-y-2">
                                <QuickLink
                                    to="/topics"
                                    title="Topics by subject & year"
                                    desc="Browse every topic and its question count."
                                    icon={BookOpen}
                                    color="bg-green-50 text-green-600"
                                />
                                <QuickLink
                                    to="/teachers"
                                    title="Teachers & students"
                                    desc="See teachers and their classes."
                                    icon={GraduationCap}
                                    color="bg-purple-50 text-purple-600"
                                />
                                <QuickLink
                                    to="/users"
                                    title="User directory"
                                    desc="All students, parents, teachers."
                                    icon={UserPlus}
                                    color="bg-blue-50 text-[#2148C0]"
                                />
                                <QuickLink
                                    to="/question-review"
                                    title="Question review"
                                    desc="Audit incorrect or missing data."
                                    icon={HelpCircle}
                                    color="bg-yellow-50 text-yellow-600"
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const QuickLink = ({ to, title, desc, icon: Icon, color }) => (
    <Link
        to={to}
        className="group flex items-start gap-3 p-3 rounded-xl hover:bg-blue-50/60 transition-all"
    >
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}>
            <Icon size={18} />
        </span>
        <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
        </div>
        <ArrowUpRight
            size={16}
            className="text-gray-300 group-hover:text-[#2148C0] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
        />
    </Link>
);

export default AdminDashboard;
